import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Link2, Trash2, Unlink, Upload, Wand2 } from 'lucide-react'
import { bankAutoMatch, bankImportCsvText, deleteBankLine, getBankCandidates, getBankData, matchBankLine, unmatchBankLine } from '../../api/accounting'
import AccountSelect from '../../components/AccountSelect'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { HelpField, HelpTip } from '../../components/HelpTip'
import { BackLink, FilterChips, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import { useControlAccounts, useStandardDefaults } from './accountingShared'

const SHOW = [{ id: 'open', label: 'Not matched' }, { id: 'matched', label: 'Matched' }, { id: 'all', label: 'All' }]
const signed = (v) => (Number(v) < 0 ? `−${money(-Number(v))}` : money(v))
const MONEY_WORDS = /cash|bank|card|petty|wallet|money|checking|current|savings|till/i

// Bank reconciliation: bring in the bank statement, match each line to the
// books, and see whether the bank and the books agree.
export default function BankRec() {
  const { notify } = useToast()
  const controls = useControlAccounts()
  const std = useStandardDefaults()
  const [cashCode, setCashCode] = useState('')
  const [data, setData] = useState(null)
  const [show, setShow] = useState('open')
  const [statementBalance, setStatementBalance] = useState('')
  const [dialog, setDialog] = useState(null)
  const [error, setError] = useState('')
  const [version, setVersion] = useState(0)
  const reload = () => setVersion((v) => v + 1)

  useEffect(() => { if (controls && !cashCode && (controls.Cash || std.Cash)) setCashCode(controls.Cash || std.Cash) }, [controls, std, cashCode])
  useEffect(() => {
    if (!cashCode) return
    getBankData({ cash_code: cashCode }).then(setData).catch((e) => setError(e.message || 'Could not load the reconciliation.'))
  }, [cashCode, version])

  const stmts = data?.statements || []
  const counts = { open: stmts.filter((s) => !s.matched_je).length, matched: stmts.filter((s) => s.matched_je).length, all: stmts.length }
  const shown = stmts.filter((s) => show === 'all' || (show === 'open' ? !s.matched_je : !!s.matched_je))
  const inBooksOnly = (data?.gl_rows || []).filter((g) => !g.matched)

  const act = async (fn, message) => {
    setError('')
    try { await fn(); notify(message); reload() } catch (e) { setError(e.message || 'That did not work.') }
  }

  return (
    <div className="ci-page">
      <BackLink to="/accounting/month-end" label="Month-end close" />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Bank reconciliation <HelpTip k="bankRec" /></h1>
            <div className="ci-title-meta">Match the bank statement to the books.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button data-write type="button" className="ci-btn ci-btn-ghost" onClick={() => setDialog({ kind: 'import' })}><Upload size={15} />Import statement</button>
          <button data-write type="button" className="ci-btn ci-btn-primary" disabled={!counts.open} onClick={() => setDialog({ kind: 'auto' })}><Wand2 size={15} />Auto-match</button>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-form-field acc-inline-field">
          <span>Bank or cash account</span>
          <AccountSelect value={cashCode} onChange={setCashCode} types={['Asset']} filter={(a, all) => {
            // Cash and bank accounts: grouped with the Cash account, or named like one.
            const cash = all.find((x) => x.account_code === (controls?.Cash || std.Cash))
            const isParent = all.some((x) => x.parent_account_id === a.account_id)
            return !isParent && (a.account_code === cash?.account_code || (cash?.parent_account_id && a.parent_account_id === cash.parent_account_id) || MONEY_WORDS.test(a.account_name))
          }} />
        </label>
      </div>

      {error && <ErrorBanner message={error} />}

      {!data ? <Spinner label="Loading the reconciliation…" /> : (
        <>
          {stmts.length > 0 && (
            <Reconciliation data={data} statementBalance={statementBalance} setStatementBalance={setStatementBalance} />
          )}

          <FilterChips options={SHOW.map((s) => ({ ...s, count: counts[s.id] }))} value={show} onChange={setShow} />

          <div className="ci-table-wrap">
            <div className="ci-table-scroll">
              <table className="ci-table fit-sm">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Statement line</th>
                    <th className="num">Amount</th>
                    <th>In the books</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <tr><td colSpan="5"><div className="ci-empty">{stmts.length ? 'Nothing here.' : 'No statement imported yet. Import one from your bank to start.'}</div></td></tr>
                  ) : shown.map((s) => (
                    <tr key={s.stmt_id}>
                      <td className="ci-muted-cell">{fmtDay(s.date)}</td>
                      <td>{s.memo || '–'}{s.ref && <span className="cell-sub">{s.ref}</span>}</td>
                      <td className="num ci-total">{signed(s.amount)}</td>
                      <td>
                        {s.matched_je
                          ? <Link to={`/accounting/journal/${s.matched_je}`}>{docNo('je', s.matched_je_no) || 'Entry'}</Link>
                          : <button data-write type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setDialog({ kind: 'match', stmt: s })}><Link2 size={13} />Match</button>}
                      </td>
                      <td className="num">
                        {s.matched_je
                          ? <button data-write type="button" className="acc-icon-btn" title="Unmatch" aria-label="Unmatch" onClick={() => act(() => unmatchBankLine(s.stmt_id), 'Unmatched')}><Unlink size={15} /></button>
                          : <button data-write type="button" className="acc-icon-btn" title="Delete this line" aria-label="Delete this line" onClick={() => { if (window.confirm('Delete this statement line?')) act(() => deleteBankLine(s.stmt_id), 'Line deleted') }}><Trash2 size={15} /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {stmts.length > 0 && (
            <section className="doc-card acc-gap">
              <div className="doc-card-head">
                <h2 className="label-with-help">In the books, not the bank <HelpTip k="inTransit" /></h2>
                <span>{inBooksOnly.length ? signed(inBooksOnly.reduce((t, g) => t + num(g.amount), 0)) : 'None'}</span>
              </div>
              {inBooksOnly.length ? (
                <div className="doc-table-scroll">
                  <table className="ci-line-table acc-view-table">
                    <thead><tr><th>Date</th><th>Entry</th><th>Description</th><th className="num">Amount</th></tr></thead>
                    <tbody>
                      {inBooksOnly.map((g) => (
                        <tr key={g.jl_id}>
                          <td>{fmtDay(g.date)}</td>
                          <td><Link to={`/accounting/journal/${g.je_id}`}>{docNo('je', g.je_no)}</Link></td>
                          <td>{g.memo || '–'}</td>
                          <td className="num">{signed(g.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="doc-empty">Every book line over the statement’s dates is matched.</p>}
            </section>
          )}
        </>
      )}

      <ImportDialog open={dialog?.kind === 'import'} onClose={() => setDialog(null)} onDone={reload} />
      <AutoDialog open={dialog?.kind === 'auto'} cashCode={cashCode} onClose={() => setDialog(null)} onDone={reload} />
      <MatchDialog stmt={dialog?.kind === 'match' ? dialog.stmt : null} cashCode={cashCode} onClose={() => setDialog(null)} onDone={reload} />
    </div>
  )
}

function Reconciliation({ data, statementBalance, setStatementBalance }) {
  const entered = String(statementBalance).trim() !== '' && Number.isFinite(Number(statementBalance))
  const stmt = num(statementBalance)
  const glOut = num(data.outstanding?.gl_total)
  const stmtOut = num(data.outstanding?.statements_total)
  const adjBank = stmt + glOut
  const adjBooks = num(data.book_balance) + stmtOut
  const diff = Math.round((adjBooks - adjBank) * 100) / 100
  return (
    <section className="acc-rec">
      <div className="acc-rec-col">
        <HelpField label={`Statement balance, ${fmtDay(data.as_of)}`} k="statementBalance">
          <input value={statementBalance} onChange={(e) => setStatementBalance(e.target.value)} inputMode="decimal" placeholder="From the statement" />
        </HelpField>
        <dl className="acc-tieout">
          <div><dt className="label-with-help">In the books, not the bank <HelpTip k="inTransit" /></dt><dd>{signed(glOut)}</dd></div>
          <div className="is-diff"><dt>Bank, adjusted</dt><dd>{entered ? signed(adjBank) : '–'}</dd></div>
        </dl>
      </div>
      <div className="acc-rec-col">
        <dl className="acc-tieout">
          <div><dt>Book balance, {fmtDay(data.as_of)}</dt><dd>{signed(data.book_balance)}</dd></div>
          <div><dt className="label-with-help">In the bank, not the books <HelpTip k="notInBooks" /></dt><dd>{signed(stmtOut)}</dd></div>
          <div className="is-diff"><dt>Books, adjusted</dt><dd>{signed(adjBooks)}</dd></div>
        </dl>
      </div>
      <div className="acc-rec-result">
        {!entered
          ? <span className="acc-rec-hint">Enter the statement balance to compare.</span>
          : Math.abs(diff) < 0.005
            ? <StatusBadge label="Reconciled" tone="good" />
            : <><span>Difference</span><strong>{signed(diff)}</strong></>}
      </div>
    </section>
  )
}

function ImportDialog({ open, onClose, onDone }) {
  const { notify } = useToast()
  const input = useRef(null)
  const [file, setFile] = useState(null)
  const [order, setOrder] = useState('mdy')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { if (open) { setFile(null); setError('') } }, [open])

  const run = async () => {
    if (!file) { setError('Choose the CSV file.'); return }
    setBusy(true)
    setError('')
    try {
      const res = await bankImportCsvText(await file.text(), order)
      const extra = [res.duplicates && `${res.duplicates} already imported`, res.skipped && `${res.skipped} couldn’t be read`].filter(Boolean).join(', ')
      notify(`Imported ${res.imported} ${res.imported === 1 ? 'line' : 'lines'}${extra ? ` (${extra})` : ''}`)
      if (res.skipped && res.problems?.length) setError(res.problems.join('. '))
      else onClose()
      onDone()
    } catch (e) {
      setError(e.message || 'Could not import the file.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<>Import a bank statement <HelpTip k="bankImport" /></>}
      subtitle="A CSV file exported from your bank."
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={run}>Import</Button>
        </>
      )}
    >
      {error && <p className="acc-modal-error">{error}</p>}
      <div className="acc-modal-stack">
        <div>
          <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => input.current?.click()}><Upload size={14} />{file ? file.name : 'Choose the CSV file'}</button>
        </div>
        <label className="ci-form-field">
          <span>Dates in the file are written</span>
          <select value={order} onChange={(e) => setOrder(e.target.value)}>
            <option value="mdy">Month first (10/14/2026)</option>
            <option value="dmy">Day first (14/10/2026)</option>
          </select>
        </label>
      </div>
    </Modal>
  )
}

function AutoDialog({ open, cashCode, onClose, onDone }) {
  const { notify } = useToast()
  const [days, setDays] = useState('3')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { if (open) setError('') }, [open])
  const run = async () => {
    setBusy(true)
    setError('')
    try {
      const res = await bankAutoMatch({ cash_code: cashCode, date_tol_days: parseInt(days, 10) || 3, amount_tol: 0.01 })
      notify(res.matched ? `Matched ${res.matched} ${res.matched === 1 ? 'line' : 'lines'}` : 'Nothing could be matched automatically')
      onClose()
      onDone()
    } catch (e) {
      setError(e.message || 'Could not match.')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<>Auto-match <HelpTip k="autoMatch" /></>}
      subtitle="Same amount, a few days apart at most."
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={run}>Match</Button>
        </>
      )}
    >
      {error && <p className="acc-modal-error">{error}</p>}
      <label className="ci-form-field">
        <span>Up to how many days apart</span>
        <input value={days} onChange={(e) => setDays(e.target.value)} inputMode="numeric" />
      </label>
    </Modal>
  )
}

function MatchDialog({ stmt, cashCode, onClose, onDone }) {
  const { notify } = useToast()
  const [options, setOptions] = useState(null)
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState('')
  useEffect(() => {
    if (!stmt) return
    setOptions(null)
    setError('')
    getBankCandidates(stmt.stmt_id, cashCode).then(setOptions).catch((e) => { setOptions([]); setError(e.message || 'Could not find book lines.') })
  }, [stmt, cashCode])
  const exact = useMemo(() => (options || []).filter((o) => o.exact), [options])

  const pick = async (o) => {
    setBusy(o.jl_id)
    setError('')
    try {
      await matchBankLine(stmt.stmt_id, o.jl_id)
      notify(`Matched to ${docNo('je', o.je_no)}`)
      onClose()
      onDone()
    } catch (e) {
      setError(e.message || 'Could not match.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Modal
      open={!!stmt}
      onClose={onClose}
      title="Match to the books"
      subtitle={stmt && `${fmtDay(stmt.date)} · ${stmt.memo || 'Statement line'} · ${signed(stmt.amount)}`}
      size="lg"
      footer={<Button variant="ghost" onClick={onClose}>Cancel</Button>}
    >
      {error && <p className="acc-modal-error">{error}</p>}
      {options === null ? <Spinner label="Finding book lines…" /> : options.length === 0 ? (
        <p className="acc-modal-text">No unmatched book lines on this account within a month of it. If it’s a bank fee or interest, record it with a <Link to="/accounting/journal/new">journal entry</Link> first.</p>
      ) : (
        <div className="acc-modal-stack">
          {exact.length === 0 && <p className="acc-modal-text">None for the same amount. The closest are below.</p>}
          <table className="ci-line-table">
            <thead><tr><th>Date</th><th>Entry</th><th>Description</th><th className="num">Amount</th><th /></tr></thead>
            <tbody>
              {options.map((o) => (
                <tr key={o.jl_id} className={o.exact ? 'acc-exact' : undefined}>
                  <td>{fmtDay(o.date)}</td>
                  <td>{docNo('je', o.je_no)}</td>
                  <td>{o.memo || '–'}</td>
                  <td className="num">{signed(o.amount)}</td>
                  <td className="num"><button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={busy !== null} onClick={() => pick(o)}><Check size={13} />{busy === o.jl_id ? 'Matching…' : 'Match'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  )
}
